/**
 * Router.
 *
 * Route skeleton:
 *   /sign-in      → Clerk hosted <SignIn/>
 *   /             → protected admin layout (Clerk session + operator role)
 *     index       → Lookup (eager; the console's first-paint surface)
 *     /accounts/:accountID → Account detail (lazy, code-split)
 *     /actions    → Admin Actions (admin role; the page itself renders an
 *                   "admin role required" panel for support operators)
 *   *             → 404
 *
 * The protected branch is a composition of two gates:
 *   <ProtectedRoute/>  — Clerk initialized + signed in (redirect to sign-in)
 *   <RequireRole/>     — GET /v1/admin/me resolves the operator role; 401/403
 *                        renders the full-page AccessDenied screen
 */
import { lazy, Suspense } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { RequireRole } from '@/auth/RequireRole';
import { AdminLayout } from '@/layouts/AdminLayout';
import { SignInRoute } from '@/routes/SignInRoute';
import { NotFoundRoute } from '@/routes/NotFoundRoute';
import LookupRoute from '@/features/lookup/LookupRoute';
import { LoadingState } from '@/components/feedback/LoadingState';

// The account surface is code-split off the first-paint path (its tabs carry
// five more data tables); lookup stays eager as the index.
const AccountRoute = lazy(() =>
  import('@/features/account/AccountRoute').then((m) => ({ default: m.AccountPage })),
);
const ActionsRoute = lazy(() =>
  import('@/features/actions/ActionsRoute').then((m) => ({ default: m.ActionsPage })),
);

export const router = createBrowserRouter([
  { path: '/sign-in', element: <SignInRoute /> },
  // Clerk catch-all auth routes redirect to the canonical sign-in.
  { path: '/sign-in/*', element: <Navigate to="/sign-in" replace /> },
  {
    path: '/',
    element: <ProtectedRoute />,
    children: [
      {
        element: <RequireRole />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              { index: true, element: <LookupRoute /> },
              {
                path: 'accounts/:accountID',
                element: (
                  <Suspense fallback={<LoadingState label="Loading account…" />}>
                    <AccountRoute />
                  </Suspense>
                ),
              },
              {
                path: 'actions',
                element: (
                  <Suspense fallback={<LoadingState label="Loading actions…" />}>
                    <ActionsRoute />
                  </Suspense>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <NotFoundRoute /> },
]);
